import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from 'react';
import {
  User,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { getAnalytics, setUserId, logEvent } from 'firebase/analytics';
import { Capacitor } from '@capacitor/core';
import { auth, googleProvider, authPersistenceReady } from '../firebase/config';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  resetSessionTimeout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const INSTALL_MARKER_KEY = 'luminaInstallMarkerV1';
const FORCE_LOGOUT_KEY = 'luminaForceLogoutV1';
const WEB_REDIRECT_PENDING_KEY = 'luminaWebRedirectPendingV1';

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }) as Promise<T>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSigningInRef = useRef(false);
  const lastSignInAttemptAtRef = useRef(0);

  // Session timeout duration (1 hour = 3600000 ms)
  const SESSION_TIMEOUT = 3600000;

  const clearSessionTimeout = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  }, []);

  const updateLastActivity = useCallback(() => {
    try {
      localStorage.setItem('lastActivityTime', Date.now().toString());
    } catch (error) {
      console.error('Error updating last activity time:', error);
    }
  }, []);

  const cleanupLocalStorage = useCallback(() => {
    try {
      const keys = ['lastActivityTime', 'downloadedMeditations', 'meditationUserName'];
      keys.forEach(key => {
        const value = localStorage.getItem(key);
        if (!value) return;

        try {
          if (key === 'downloadedMeditations') {
            const parsed = JSON.parse(value);
            if (!Array.isArray(parsed)) {
              console.log(`Clearing corrupted ${key} data`);
              localStorage.removeItem(key);
            }
          } else if (key === 'lastActivityTime') {
            const parsed = parseInt(value, 10);
            if (isNaN(parsed) || parsed <= 0) {
              console.log(`Clearing corrupted ${key} data`);
              localStorage.removeItem(key);
            }
          }
        } catch (parseError) {
          console.log(`Clearing corrupted ${key} data:`, parseError);
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error during localStorage cleanup:', error);
    }
  }, []);

  const shouldBlockAutoSignIn = useCallback(() => {
    try {
      return localStorage.getItem(FORCE_LOGOUT_KEY) === '1';
    } catch (error) {
      console.warn('Could not read force-logout flag:', error);
      return false;
    }
  }, []);

  const isFirstLaunchAfterInstall = useCallback(() => {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const marker = localStorage.getItem(INSTALL_MARKER_KEY);
      if (marker) return false;
      localStorage.setItem(INSTALL_MARKER_KEY, Date.now().toString());
      return true;
    } catch (error) {
      console.warn('Could not read install marker:', error);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    let nativeError: unknown = null;
    let webError: unknown = null;

    try {
      if (Capacitor.isNativePlatform()) {
        try {
          await FirebaseAuthentication.signOut();
        } catch (error) {
          nativeError = error;
          console.error('Native sign-out failed:', error);
        }
      }

      try {
        await signOut(auth);
      } catch (error) {
        webError = error;
        console.error('Web Firebase sign-out failed:', error);
      }

      clearSessionTimeout();
      localStorage.removeItem('lastActivityTime');
      localStorage.setItem(FORCE_LOGOUT_KEY, '1');
      setCurrentUser(null);
      setLoading(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }

    if (nativeError && webError) {
      throw new Error('Sign-out failed on both native and web auth providers.');
    }
  }, [clearSessionTimeout]);

  const startSessionTimeout = useCallback(() => {
    clearSessionTimeout();

    sessionTimeoutRef.current = setTimeout(() => {
      console.log('Session timeout reached - logging out user');
      void logout();
    }, SESSION_TIMEOUT);
  }, [clearSessionTimeout, logout]);

  const checkSessionExpiry = useCallback((user?: User | null) => {
    try {
      const lastActivity = localStorage.getItem('lastActivityTime');
      if (lastActivity) {
        const lastActivityTime = parseInt(lastActivity, 10);

        if (isNaN(lastActivityTime) || lastActivityTime <= 0) {
          console.log('Invalid lastActivityTime found - clearing corrupted data');
          localStorage.removeItem('lastActivityTime');
          return false;
        }

        const timeSinceLastActivity = Date.now() - lastActivityTime;
        if (timeSinceLastActivity > SESSION_TIMEOUT) {
          const parsedLastSignInTime = user?.metadata?.lastSignInTime
            ? Date.parse(user.metadata.lastSignInTime)
            : NaN;

          // If sign-in metadata is unavailable/invalid, avoid false logouts after redirect flows.
          if (Number.isNaN(parsedLastSignInTime)) {
            localStorage.removeItem('lastActivityTime');
            return false;
          }

          // Fresh sign-in should not be treated as an expired previous session.
          if (parsedLastSignInTime > lastActivityTime) {
            localStorage.removeItem('lastActivityTime');
            return false;
          }

          console.log('Session expired since last visit - clearing session');
          clearSessionTimeout();
          localStorage.removeItem('lastActivityTime');
          return true;
        }
      }
    } catch (error) {
      console.error('Error checking session expiry:', error);
      localStorage.removeItem('lastActivityTime');
    }

    return false;
  }, [clearSessionTimeout]);

  const resetSessionTimeout = useCallback(() => {
    if (currentUser) {
      updateLastActivity();
      startSessionTimeout();
    }
  }, [currentUser, startSessionTimeout, updateLastActivity]);

  const mapNativeUserToWebUser = useCallback((nativeUser: any): User => {
    const creationTime = nativeUser?.metadata?.creationTime
      ? new Date(nativeUser.metadata.creationTime).toISOString()
      : undefined;
    const lastSignInTime = nativeUser?.metadata?.lastSignInTime
      ? new Date(nativeUser.metadata.lastSignInTime).toISOString()
      : undefined;

    return {
      uid: nativeUser?.uid ?? '',
      email: nativeUser?.email ?? null,
      displayName: nativeUser?.displayName ?? null,
      photoURL: nativeUser?.photoUrl ?? nativeUser?.photoURL ?? null,
      metadata: {
        creationTime,
        lastSignInTime
      }
    } as unknown as User;
  }, []);

  useEffect(() => {
    cleanupLocalStorage();

    const loadingFallback = setTimeout(() => {
      setLoading(false);
      console.warn('Auth initialization timeout - showing login screen fallback');
    }, 8000);

    if (Capacitor.isNativePlatform()) {
      let nativeAuthListener: { remove: () => Promise<void> } | null = null;

      const applyNativeUser = (nativeUser: any | null) => {
        if (shouldBlockAutoSignIn()) {
          if (nativeUser) {
            void FirebaseAuthentication.signOut().catch((error) => {
              console.warn('Failed to clear native session after explicit logout:', error);
            });
          }
          setCurrentUser(null);
          setLoading(false);
          clearTimeout(loadingFallback);
          clearSessionTimeout();
          localStorage.removeItem('lastActivityTime');
          return;
        }

        const mappedUser = nativeUser ? mapNativeUserToWebUser(nativeUser) : null;
        const justSignedIn = Date.now() - lastSignInAttemptAtRef.current < 30000;

        if (mappedUser && !isSigningInRef.current && !justSignedIn && checkSessionExpiry(mappedUser)) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        setCurrentUser(mappedUser);
        setLoading(false);
        clearTimeout(loadingFallback);

        if (mappedUser) {
          updateLastActivity();
          startSessionTimeout();
        } else {
          clearSessionTimeout();
          localStorage.removeItem('lastActivityTime');
        }
      };

      const initializeNativeAuth = async () => {
        if (isFirstLaunchAfterInstall()) {
          console.log('First launch after install detected - clearing native auth session');
          try {
            await FirebaseAuthentication.signOut();
          } catch (error) {
            console.warn('Native sign-out on first launch failed:', error);
          }
          try {
            await signOut(auth);
          } catch (error) {
            console.warn('Web sign-out on first launch failed:', error);
          }
          localStorage.setItem(FORCE_LOGOUT_KEY, '1');
          applyNativeUser(null);
        }

        try {
          const current = await withTimeout(
            FirebaseAuthentication.getCurrentUser(),
            10000,
            'Native auth state check timed out.'
          );
          applyNativeUser(current.user ?? null);
        } catch (error) {
          console.error('Error getting native auth user:', error);
          setCurrentUser(null);
          setLoading(false);
          clearTimeout(loadingFallback);
        }

        try {
          nativeAuthListener = await FirebaseAuthentication.addListener('authStateChange', (event: any) => {
            applyNativeUser(event?.user ?? null);
          });
        } catch (error) {
          console.error('Error adding native auth listener:', error);
        }
      };

      void initializeNativeAuth();

      return () => {
        clearTimeout(loadingFallback);
        if (nativeAuthListener) {
          void nativeAuthListener.remove();
        }
      };
    }

    const handleRedirectResult = async () => {
      if (!Capacitor.isNativePlatform()) {
        try {
          await authPersistenceReady;
          const result = await getRedirectResult(auth);
          if (result) {
            console.log('User signed in via redirect');
            localStorage.removeItem(WEB_REDIRECT_PENDING_KEY);
            lastSignInAttemptAtRef.current = Date.now();
          }
        } catch (error) {
          console.error('Error handling redirect result:', error);
        }
      }
    };

    void handleRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      await new Promise(resolve => setTimeout(resolve, 100));

      const justSignedIn = Date.now() - lastSignInAttemptAtRef.current < 30000;
      const pendingRedirectAt = Number(localStorage.getItem(WEB_REDIRECT_PENDING_KEY) || 0);
      const justReturnedFromRedirect =
        Number.isFinite(pendingRedirectAt) && pendingRedirectAt > 0 && (Date.now() - pendingRedirectAt < 5 * 60 * 1000);

      if (user && justReturnedFromRedirect) {
        localStorage.removeItem(WEB_REDIRECT_PENDING_KEY);
        lastSignInAttemptAtRef.current = Date.now();
      }

      if (user && !isSigningInRef.current && !justSignedIn && !justReturnedFromRedirect && checkSessionExpiry(user)) {
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      setCurrentUser(user);
      setLoading(false);
      clearTimeout(loadingFallback);

      if (user) {
        try {
          const analyticsInstance = await getAnalytics();
          setUserId(analyticsInstance, user.uid);

          const first = new Date(user.metadata.creationTime!).getTime();
          const last = new Date(user.metadata.lastSignInTime!).getTime();
          if (first === last) {
            logEvent(analyticsInstance, 'signup', {
              method: 'google',
              timestamp: new Date().toISOString()
            });
          }

          logEvent(analyticsInstance, 'user_properties_set', {
            user_id: user.uid,
            email: user.email,
            display_name: user.displayName,
            signup_date: user.metadata.creationTime,
            last_signin: user.metadata.lastSignInTime
          });

          updateLastActivity();
          startSessionTimeout();
        } catch (error) {
          console.error('Error setting analytics user ID:', error);
        }
      } else {
        clearSessionTimeout();
        localStorage.removeItem('lastActivityTime');
      }
    }, (error) => {
      console.error('Auth state observer error:', error);
      setCurrentUser(null);
      setLoading(false);
      clearTimeout(loadingFallback);
    });

    return () => {
      clearTimeout(loadingFallback);
      unsubscribe();
    };
  }, [checkSessionExpiry, cleanupLocalStorage, clearSessionTimeout, isFirstLaunchAfterInstall, mapNativeUserToWebUser, shouldBlockAutoSignIn, startSessionTimeout, updateLastActivity]);

  useEffect(() => {
    if (!currentUser) return;

    const handleUserActivity = () => {
      resetSessionTimeout();
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser) {
        if (checkSessionExpiry()) {
          return;
        }
        updateLastActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSessionExpiry, currentUser, resetSessionTimeout, updateLastActivity]);

  const signInWithGoogle = useCallback(async () => {
    isSigningInRef.current = true;
    lastSignInAttemptAtRef.current = Date.now();
    try {
      localStorage.removeItem(FORCE_LOGOUT_KEY);
      localStorage.removeItem('lastActivityTime');

      if (Capacitor.isNativePlatform()) {
        const result = await withTimeout(
          FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: false }),
          30000,
          'Google sign-in timed out. Please try again.'
        );
        const nativeUser = (result as any)?.user ?? null;
        if (!nativeUser) {
          const current = await withTimeout(
            FirebaseAuthentication.getCurrentUser(),
            10000,
            'Could not read native signed-in user.'
          );
          if (!current.user) {
            throw new Error('Native Google sign-in did not return a user.');
          }
          setCurrentUser(mapNativeUserToWebUser(current.user));
        } else {
          setCurrentUser(mapNativeUserToWebUser(nativeUser));
        }
        setLoading(false);
        updateLastActivity();
        startSessionTimeout();
      } else {
        await authPersistenceReady;

        try {
          await withTimeout(
            signInWithPopup(auth, googleProvider),
            30000,
            'Google sign-in timed out. Please try again.'
          );
          return;
        } catch (popupError: any) {
          const popupCode = popupError?.code || '';
          const shouldFallbackToRedirect = [
            'auth/popup-blocked',
            'auth/popup-closed-by-user',
            'auth/cancelled-popup-request',
            'auth/operation-not-supported-in-this-environment'
          ].includes(popupCode);

          if (!shouldFallbackToRedirect) {
            throw popupError;
          }
          console.warn('Popup sign-in failed, falling back to redirect:', popupCode);
        }

        localStorage.setItem(WEB_REDIRECT_PENDING_KEY, Date.now().toString());
        await signInWithRedirect(auth, googleProvider);
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    } finally {
      isSigningInRef.current = false;
    }
  }, [mapNativeUserToWebUser, startSessionTimeout, updateLastActivity]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (Capacitor.isNativePlatform()) {
      try {
        const refreshedTokenResult = await withTimeout(
          FirebaseAuthentication.getIdToken({ forceRefresh: true }),
          10000,
          'Refreshing native ID token timed out.'
        );
        if (refreshedTokenResult.token) {
          return refreshedTokenResult.token;
        }
      } catch (error) {
        console.warn('Error refreshing native ID token, retrying with cached token:', error);
      }

      try {
        const cachedTokenResult = await withTimeout(
          FirebaseAuthentication.getIdToken({ forceRefresh: false }),
          10000,
          'Fetching cached native ID token timed out.'
        );
        return cachedTokenResult.token ?? null;
      } catch (error) {
        console.error('Error getting native ID token:', error);
        return null;
      }
    }

    if (!currentUser) return null;
    try {
      return await currentUser.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }, [currentUser]);

  const value: AuthContextType = {
    currentUser,
    loading,
    signInWithGoogle,
    logout,
    getIdToken,
    resetSessionTimeout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
